/**
 * Garment imagery for the operator console.
 *
 * Admin signs object refs on read and a signature lasts fifteen minutes, so a
 * picture here has three distinct ways of not being a picture and they must
 * not look alike: a ref returned without a signed URL, a signed URL that did
 * not load, and genuinely absent (no ref at all). Each gets its own
 * placeholder saying which it is.
 *
 * The image element below resolves on failure as well as on success. The
 * consumer app shipped an outfit card whose skeleton waited on a load event
 * alone and span forever on a dead URL; nothing here may resolve on only one
 * of the two events.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type FC,
  type ReactNode,
} from 'react';
import { Hourglass03, Image01, ImageX, Lock01 } from '@untitledui/icons';
import { Button } from '@ui/components/base/buttons/button';
import { Callout } from '@ui/console/primitives';
import { cx } from '@ui/utils/cx';

/** `storage.signed_url(ref, expires_seconds=900)` — the backend's own figure. */
export const SIGNED_URL_TTL_MS = 900_000;

type SignatureFailureReporter = () => void;

const SignatureFailureContext = createContext<SignatureFailureReporter | undefined>(undefined);

/** A ref without a signed URL is distinct from a garment with no image ref. */
function hasImageRefWithoutSignedUrl(
  url: string | null | undefined,
  ref: string | null | undefined,
) {
  return !url && Boolean(ref);
}

function Placeholder({
  icon: Icon,
  title,
  detail,
  action,
}: {
  icon: FC<{ className?: string }>;
  title: string;
  detail: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-1 px-2 py-3 text-center">
      <Icon className="size-5 text-fg-quaternary" />
      <span className="text-xs font-medium text-tertiary">{title}</span>
      <span className="text-xs text-quaternary">{detail}</span>
      {action}
    </div>
  );
}

/**
 * One signed image, or the observable state preventing one from being shown.
 * `refPresent` separates a returned ref without a signed URL from a garment
 * with no image ref.
 *
 * `originalUrl` is the member's own upload. Ingestion records the processed
 * ref before the object behind it exists, so a garment still being made has a
 * processed URL that 404s: that is "not produced yet", not "unavailable".
 * When the processed image is missing or fails, the upload is shown instead,
 * with a note saying which picture it is. "Image unavailable" (and its
 * Refresh links) is left for a picture that should exist and did not load.
 */
export function GarmentImage({
  url,
  refPresent,
  originalUrl,
  pending = false,
  alt,
  className,
  absentTitle = 'No image',
  absentDetail = 'Nothing digitised for this garment.',
  onRefresh,
}: {
  url?: string | null;
  refPresent?: string | null;
  /** Signed `original_image_refs[0]`, shown when the processed image is not there. */
  originalUrl?: string | null;
  /** Ingestion or enhancement is still running, so the processed image may not exist yet. */
  pending?: boolean;
  alt: string;
  className?: string;
  /** What the "no image on record" placeholder says, for a slot with its own meaning. */
  absentTitle?: string;
  absentDetail?: string;
  /** Re-runs the read that signs the refs, for when a signature has aged out. */
  onRefresh?: (() => void | Promise<void>) | undefined;
}) {
  const [phase, setPhase] = useState<'loading' | 'loaded' | 'failed'>('loading');
  const [displayed, setDisplayed] = useState<{ ref?: string | null; url?: string | null }>(() => ({
    ref: refPresent,
    url,
  }));
  const [processedFailed, setProcessedFailed] = useState(false);
  // Like the processed URL, a re-signed upload URL is the same object: keep
  // the one already decoded unless it is the one that failed.
  const [shownOriginal, setShownOriginal] = useState(originalUrl);
  const reportSignatureFailure = useContext(SignatureFailureContext);
  const refresh = () => void Promise.resolve(onRefresh?.()).catch(() => undefined);
  // Polling mints a different signed URL for an unchanged object ref. Keep the
  // image the browser has already decoded in place; swap only when the object
  // itself changes (or when replacing a URL that actually failed).
  useEffect(() => {
    if (refPresent === displayed.ref && url === displayed.url) return;
    const sameObject = Boolean(refPresent) && refPresent === displayed.ref;
    if (sameObject && displayed.url && phase !== 'failed' && !processedFailed) return;
    setDisplayed({ ref: refPresent, url });
    setProcessedFailed(false);
  }, [displayed.ref, displayed.url, phase, processedFailed, refPresent, url]);
  useEffect(() => {
    if (!originalUrl || (shownOriginal && phase !== 'failed')) return;
    setShownOriginal(originalUrl);
  }, [originalUrl, phase, shownOriginal]);

  const showingOriginal = Boolean(shownOriginal) && (processedFailed || !displayed.url);
  const displayedUrl = showingOriginal ? shownOriginal : displayed.url;
  useEffect(() => setPhase('loading'), [displayedUrl]);

  const frame = cx(
    'relative flex aspect-square w-full items-center justify-center overflow-hidden rounded-lg border border-secondary bg-secondary',
    className,
  );

  if (!displayedUrl) {
    const unsigned = hasImageRefWithoutSignedUrl(displayedUrl, refPresent);
    return (
      <div
        className={frame}
        data-image-state={unsigned ? 'unsigned' : 'absent'}
        role="img"
        aria-label={
          unsigned
            ? `${alt} — image reference returned without a signed URL`
            : `${alt} — no image on record`
        }
      >
        {unsigned ? (
          <Placeholder
            icon={Lock01}
            title="Signed URL missing"
            detail="This read returned an image reference but no signed URL, so the image cannot be displayed."
            action={
              onRefresh ? (
                <Button size="sm" color="link-gray" onClick={refresh}>
                  Reload
                </Button>
              ) : undefined
            }
          />
        ) : (
          <Placeholder icon={Image01} title={absentTitle} detail={absentDetail} />
        )}
      </div>
    );
  }

  return (
    <div className={frame} data-image-state={phase}>
      {phase === 'loading' ? (
        // Removed by either event below, never by onLoad alone.
        <div className="absolute inset-0 animate-pulse bg-tertiary" aria-hidden="true" />
      ) : null}
      <img
        src={displayedUrl}
        alt={alt}
        loading="lazy"
        decoding="async"
        onLoad={() => setPhase('loaded')}
        onError={() => {
          if (!showingOriginal && shownOriginal) {
            // The processed object is not there (yet): show the upload.
            setProcessedFailed(true);
            return;
          }
          setPhase('failed');
          if (!(pending && !showingOriginal)) reportSignatureFailure?.();
        }}
        className={cx('h-full w-full object-contain', phase !== 'loaded' && 'invisible')}
      />
      {showingOriginal && phase === 'loaded' ? (
        <span
          className="absolute inset-x-0 bottom-0 bg-primary/85 px-2 py-1 text-center text-xs text-tertiary"
          data-image-source="original"
        >
          {pending
            ? 'Processed image isn’t ready yet — showing the original upload.'
            : 'No processed image — showing the original upload.'}
        </span>
      ) : null}
      {phase === 'failed' && pending && !showingOriginal ? (
        <div className="absolute inset-0 flex items-center justify-center bg-secondary">
          <Placeholder
            icon={Hourglass03}
            title="Still processing"
            detail="The processed image isn’t ready yet."
          />
        </div>
      ) : phase === 'failed' ? (
        <div className="absolute inset-0 flex items-center justify-center bg-secondary">
          <Placeholder
            icon={ImageX}
            title="Image unavailable"
            detail="A signed URL was returned, but the image could not be displayed."
            action={
              onRefresh ? (
                <Button size="sm" color="link-gray" onClick={refresh}>
                  Refresh links
                </Button>
              ) : undefined
            }
          />
        </div>
      ) : null}
    </div>
  );
}

/**
 * Said once per screen, rather than letting every tile fail separately: the
 * records are still accurate, only the pictures need a fresh read.
 */
export function SignatureExpiryNotice({ onRefresh }: { onRefresh: () => void }) {
  return (
    <Callout
      tone="warning"
      title="These images could not be refreshed."
      actions={
        <Button size="sm" color="secondary" onClick={onRefresh}>
          Refresh images
        </Button>
      }
    >
      An expired image link was refreshed automatically, but the fresh read or its replacement
      images still failed. Everything else on this screen is still current.
    </Callout>
  );
}

/**
 * One read signs one image batch. An old batch is refreshed only when an
 * image actually fails, and a burst of failures shares the same refresh.
 */
export function SignatureExpiryBoundary({
  signedAt,
  onRefresh,
  ttlMs = SIGNED_URL_TTL_MS,
  children,
}: {
  signedAt: number | undefined;
  onRefresh: () => void | Promise<void>;
  /** How long this read's signatures last, for reads signed shorter than the default. */
  ttlMs?: number;
  children: ReactNode;
}) {
  const [warning, setWarning] = useState(false);
  const batch = useRef<{
    signedAt: number | undefined;
    phase: 'idle' | 'refreshing' | 'refreshed';
  }>({ signedAt, phase: 'idle' });

  useEffect(() => {
    if (signedAt !== batch.current.signedAt) {
      batch.current = {
        signedAt,
        phase: batch.current.phase === 'refreshing' ? 'refreshed' : 'idle',
      };
      setWarning(false);
    }
  }, [signedAt]);

  const reportFailure = useCallback(() => {
    const current = batch.current;
    if (current.phase === 'refreshing') return;
    if (current.phase === 'refreshed') {
      setWarning(true);
      return;
    }
    if (!signedAt || Date.now() - signedAt < ttlMs) return;

    // Set the ref before invoking the read: sibling image errors in the same
    // browser turn must coalesce into this one request.
    batch.current = { signedAt, phase: 'refreshing' };
    Promise.resolve(onRefresh()).catch(() => {
      batch.current = { signedAt, phase: 'refreshed' };
      setWarning(true);
    });
  }, [onRefresh, signedAt, ttlMs]);

  const manualRefresh = useCallback(() => {
    setWarning(false);
    batch.current = { signedAt, phase: 'refreshing' };
    Promise.resolve(onRefresh()).catch(() => {
      batch.current = { signedAt, phase: 'refreshed' };
      setWarning(true);
    });
  }, [onRefresh, signedAt]);

  return (
    <SignatureFailureContext.Provider value={reportFailure}>
      <div className="contents">
        {warning ? <SignatureExpiryNotice onRefresh={manualRefresh} /> : null}
        {children}
      </div>
    </SignatureFailureContext.Provider>
  );
}

/**
 * The 24px thumbnail beside a named reference. Too small for GarmentImage's
 * placeholders, so a failed load hands back to the caller (initials, or
 * nothing) instead - but it still settles on error as well as on load.
 */
export function RefThumbnail({
  src,
  round = false,
  onFailed,
}: {
  src: string;
  round?: boolean;
  onFailed: () => void;
}) {
  const [loaded, setLoaded] = useState(false);
  return (
    <img
      src={src}
      alt=""
      loading="lazy"
      decoding="async"
      onLoad={() => setLoaded(true)}
      onError={onFailed}
      className={cx(
        'size-6 shrink-0 bg-secondary object-cover',
        round ? 'rounded-full' : 'rounded-md',
        !loaded && 'animate-pulse',
      )}
    />
  );
}
